import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { jobNoteService } from 'jobs/services';

export const useJobPartNoteActions = ({
  notificationPosition = NotificationPosition.FORM,
}: {
  notificationPosition?: NotificationPosition;
} = {}) => ({
  ...useAddJobPartNotes(),
  ...useUpsertJobPartNote(),
  ...useRemoveJobPartNotes({ notificationPosition }),
});

const useAddJobPartNotes = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageJobPartNotesCreateSuccess') },
    loading: { message: t('messageJobPartNotesCreateInProgress') },
    error: { title: t('errorTitleJobPartNotesCreate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(jobNoteService.addJobPartNotes, {
    ...errors,
  });

  return { addJobPartNotes: mutateAsync };
};

const useUpsertJobPartNote = () => {
  const { errors } = useHandleErrors({
    success: ({ componentNote }) =>
      componentNote.id
        ? { message: t('messageJobPartNoteUpdateSuccess') }
        : { message: t('messageJobPartNoteCreateSuccess') },
    loading: ({ componentNote }) =>
      componentNote.id
        ? { message: t('messageJobPartNoteUpdateInProgress') }
        : { message: t('messageJobPartNoteCreateInProgress') },
    error: ({ componentNote }) =>
      componentNote.id
        ? { title: t('errorTitleJobPartNoteUpdate') }
        : { title: t('errorTitleJobPartNoteCreate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(jobNoteService.updateJobPartNote, {
    ...errors,
  });

  return { upsertJobPartNote: mutateAsync };
};

const useRemoveJobPartNotes = ({
  notificationPosition = NotificationPosition.FORM,
}: {
  notificationPosition?: NotificationPosition;
}) => {
  const { errors } = useHandleErrors({
    success: ({ ids }) => ({
      message: t('messageJobPartNotesRemoveSuccess', { count: ids.length }),
    }),
    loading: ({ ids }) => ({
      message: t('messageJobPartNotesRemoveInProgress', { count: ids.length }),
    }),
    error: ({ ids }) => ({
      title: t('errorTitleJobPartNotesRemove', { count: ids.length }),
    }),
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(jobNoteService.removeJobPartNote, {
    ...errors,
  });

  return { removeJobPartNotes: mutateAsync };
};
